import type { AdministratieRow, WorkerRow } from '@/api/administratie';
import type { LimosaDialogPerson } from './LimosaDialog';
import type { DocumentenPerson } from './PersoonsdocumentenDialog';
import { ToggleCell } from './ToggleCell';
import { LimosaBadge, verplichtBronLabel } from './LimosaBadge';
import { WorkerAddPicker } from '@/components/werkers/WorkerAddPicker';

/** Actions on one worker of a subcontractor row. */
export interface WorkerActions {
  busyKeys: Set<string>;
  onAanwezig: (row: AdministratieRow, workerId: number, value: boolean) => void;
  onWorkerIngecheckt: (row: AdministratieRow, workerId: number, value: boolean) => void;
  /** `person`: opened from one worker's row, so the dialog is about that worker. */
  onLimosa: (row: AdministratieRow, person?: LimosaDialogPerson) => void;
  /** Absent: no documents per person. */
  onDocumenten?: (person: DocumentenPerson) => void;
}

/** Opens one person's documents (A1, VCA, Identiteitsbewijs, …). */
export function DocumentenKnop({ person, actions }: { person: DocumentenPerson; actions: WorkerActions }) {
  if (!actions.onDocumenten) return null;
  const open = actions.onDocumenten;
  return (
    <button
      type="button"
      onClick={() => open(person)}
      aria-label={`Documenten van ${person.name}`}
      className="block mt-0.5 text-[11px] font-semibold text-td-blue hover:underline whitespace-nowrap"
    >
      Documenten
    </button>
  );
}

/**
 * The workers listed under a company row: only who was there (present), plus
 * anyone still checked in so a check-in is never hidden. The rest of the
 * company is reachable through "+ medewerker".
 */
export function listedWorkers(row: AdministratieRow): WorkerRow[] {
  return (row.workers ?? []).filter((w) => w.aanwezig === true || w.ingecheckt === true);
}

const aanwezigBusy = (row: AdministratieRow, workerId: number, actions: WorkerActions) =>
  actions.busyKeys.has(`aanwezig-${row.taskId}-${workerId}`);

function workerCells(row: AdministratieRow, w: WorkerRow, actions: WorkerActions) {
  const remove = (
    <button
      type="button"
      onClick={() => actions.onAanwezig(row, w.id, false)}
      disabled={aanwezigBusy(row, w.id, actions) || w.aanwezig !== true}
      aria-label={`${w.name} afwezig zetten`}
      className="ml-1 inline-flex items-center justify-center w-7 h-7 rounded-full text-td-muted hover:bg-gray-200 hover:text-td-navy disabled:opacity-40"
    >
      ×
    </button>
  );
  const ingecheckt = (
    <ToggleCell
      value={w.ingecheckt}
      label={`Ingecheckt ${w.name}`}
      busy={actions.busyKeys.has(`ingecheckt-${row.taskId}-${w.id}`)}
      onToggle={(v) => actions.onWorkerIngecheckt(row, w.id, v)}
    />
  );
  // The badge opens the Limosa dialog for this worker of the installer.
  const limosa = (
    <>
      <LimosaBadge row={{ ...row, limosa: w.limosa }} onOpen={(r) => actions.onLimosa(r, { id: w.id, name: w.name })} />
      {w.verplicht && <span className="block mt-0.5 text-[11px] text-td-muted whitespace-nowrap">{verplichtBronLabel(w.verplicht)}</span>}
    </>
  );
  const documenten = <DocumentenKnop person={{ id: w.id, name: w.name }} actions={actions} />;
  return { remove, ingecheckt, limosa, documenten };
}

function AddWorker({ row, actions }: { row: AdministratieRow; actions: WorkerActions }) {
  const workers = row.workers ?? [];
  // Presence unknown in this Odoo database: nothing to pick yet.
  if (workers.some((w) => w.aanwezig === null)) return <span className="text-xs text-td-light">Aanwezigheid onbekend</span>;
  return (
    <WorkerAddPicker
      workers={workers}
      presentIds={new Set(workers.filter((w) => w.aanwezig === true).map((w) => w.id))}
      onPick={(id) => actions.onAanwezig(row, id, true)}
      isBusy={(id) => aanwezigBusy(row, id, actions)}
    />
  );
}

/** Desktop: one table row per present worker under the company row, then "+ medewerker". */
export function WorkerTableRows({ row, actions }: { row: AdministratieRow; actions: WorkerActions }) {
  if (!row.workers) return null;
  return (
    <>
      {listedWorkers(row).map((w) => {
        const c = workerCells(row, w, actions);
        return (
          <tr key={`${row.key}-w${w.id}`} className="align-middle border-t border-gray-50 bg-gray-50/60 text-gray-600" data-testid="worker-row">
            <td className="px-3 py-1.5" />
            <td className="px-3 py-1.5 pl-8 text-sm">↳ {w.name}{c.remove}</td>
            <td className="px-3 py-1.5 text-xs">{w.aanwezig ? 'aanwezig' : 'niet aanwezig'}</td>
            <td className="px-3 py-1.5" />
            <td className="px-3 py-1.5">{c.ingecheckt}</td>
            <td className="px-3 py-1.5">{c.limosa}{c.documenten}</td>
          </tr>
        );
      })}
      <tr className="bg-gray-50/60" data-testid="worker-add-row">
        <td className="px-3 py-1.5" />
        <td className="px-3 py-1.5 pl-8" colSpan={5}><AddWorker row={row} actions={actions} /></td>
      </tr>
    </>
  );
}

/** Mobile: the present workers inside the company's entry, then "+ medewerker". */
export function WorkerCardList({ row, actions }: { row: AdministratieRow; actions: WorkerActions }) {
  if (!row.workers) return null;
  return (
    <ul className="mt-2 ml-3 pl-3 border-l-2 border-gray-200 space-y-2">
      {listedWorkers(row).map((w) => {
        const c = workerCells(row, w, actions);
        return (
          <li key={w.id} className="text-xs text-td-muted" data-testid="worker-card-row">
            <p className="text-sm text-gray-700 flex items-center">{w.name}{c.remove}</p>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="flex items-center gap-2">Ingecheckt {c.ingecheckt}</span>
              <span className="flex items-center gap-2">Limosa {c.limosa}</span>
              {c.documenten}
            </div>
          </li>
        );
      })}
      <li><AddWorker row={row} actions={actions} /></li>
    </ul>
  );
}

/** The subcontractor row's own Aanwezig / Ingecheckt cells when it has workers. */
export function companySummary(row: AdministratieRow) {
  const present = (row.workers ?? []).filter((w) => w.aanwezig === true).length;
  return {
    aanwezig: row.presenceReported
      ? <span className="text-xs text-gray-600">{present} aanwezig</span>
      // Decision 15: until somebody is reported, the Limosa badge judges every worker.
      : <span className="text-xs text-amber-700">nog niemand gemeld · Limosa: alle medewerkers</span>,
    ingecheckt: row.ingecheckt === true
      ? <span className="text-xs text-gray-600">ingecheckt (oud, zonder medewerker)</span>
      : <span className="text-xs text-td-light">per medewerker</span>,
  };
}
